import { useState, useEffect } from 'react';
import { useQuery, UseQueryResult } from '@taxdown/zeno-sama-query';
import { AxiosError } from 'axios';
import { PueblaPreviousYearsResponse, SatResponseType } from 'Services/sat/types';
import SatService from 'Services/sat';
import Notify, { AlertTypes } from 'Services/notification';
import StorageService from 'Services/storage';
import RecordsService from 'Services/records';
import { getErrorDetail } from 'Services/errors';
import { history } from 'Store';
import { useGetQuery } from 'Hooks/useGetQuery';
import { PreviousYearStatus } from 'Containers/PreviousYears/types';
import { TaxReturnTypes } from 'Containers/ResultXml/types';
import { stages } from 'Constants/stages';
import { routes } from 'Constants/routes';
import {
  ErrorResponseType,
  QueryKeys,
  RecordArchiveFlowResponse,
  RecordArchiveResponse,
  ResponseType,
  ResultType,
} from 'Types/common';

const MAXIMUM_AMOUNT_TO_RETURN = 1700;
const MINIMUM_AMOUNT_TO_RETURN = 1500;

export const useFetchGetXmlRecord = (
  isAnualDeclaration?: boolean
): UseQueryResult<RecordArchiveFlowResponse> => {
  const isPreviousYear = StorageService.getItem('isPreviousYear');
  return useQuery([QueryKeys.FETCH_GET_XML_RECORD], RecordsService.getFlowFeatureFlagIsActive, {
    refetchOnWindowFocus: false,
    refetchInterval: data => {
      if (!data?.ready) {
        return 500;
      }
      return false;
    },
    enabled: Boolean(isPreviousYear) || isAnualDeclaration,
  });
};

export const resultSelector = (response: RecordArchiveResponse): ResultType => {
  const dataFiltered = {
    mandatory: false,
    taxReturnResult: response.satInfo.satInfoResult.amount,
    taxReturnSubType: '',
    taxReturnType:
      response.satInfo.satInfoResult.type === TaxReturnTypes.ZERO
        ? TaxReturnTypes.EQUAL
        : response.satInfo.satInfoResult.type,
    warnings: [],
    recordId: response.id,
    flexUser:
      response.satInfo.satInfoResult.amount >= MAXIMUM_AMOUNT_TO_RETURN ||
      response.satInfo.satInfoResult.amount <= MINIMUM_AMOUNT_TO_RETURN,
  } as ResultType;

  return dataFiltered;
};
export const useFetchGetRecordResultXml = (isRecordReady?: boolean): UseQueryResult<ResultType> =>
  useQuery([QueryKeys.FETCH_GET_RECORD_RESULT_XML], RecordsService.getRecordArchive, {
    select: data => resultSelector(data),
    onError: (err: AxiosError) => {
      if (err.response?.status) {
        Notify.sendNotification(getErrorDetail(err), AlertTypes.error);
        history.push(routes.DASHBOARD);
      }
    },
    refetchOnWindowFocus: false,
    refetchInterval: data => (data ? false : 500),
    enabled: isRecordReady,
  });

export const downloadDataAnualDeclaration = (
  data?: ResponseType<SatResponseType>
): number | false => {
  const response = data?.data;
  if (!response) {
    return 500;
  }
  const redirectStages = [stages.DATA_PARTIAL_PULLED, stages.DATA_PULLED];

  if (redirectStages.includes(response.item?.stage?.value)) {
    return false;
  }
  return 500;
};

export const useFetchPollingQuizAnualDeclaration = (
  isAnualDeclaration?: boolean
): UseQueryResult<ResponseType<SatResponseType>, boolean> =>
  useQuery([QueryKeys.ANUAL_DECLARATION_QUIZ], () => SatService.getSatUser(), {
    refetchInterval: data => downloadDataAnualDeclaration(data),
    refetchOnWindowFocus: false,
    enabled: isAnualDeclaration,
  });
export const useFetchPollingQuizPreviousYears = (
  isPreviousYears?: boolean
): UseQueryResult<ResponseType<PueblaPreviousYearsResponse>> =>
  useQuery([QueryKeys.PREVIOUS_YEARS_QUIZ], SatService.getPueblaPreviousYears, {
    onError: (error: ErrorResponseType) => {
      const errorData = error;
      const errorDetail = errorData?.response?.data?.detail;
      Notify.sendNotification(errorDetail, AlertTypes.error);
    },
    refetchInterval: data => {
      const response = data?.data;
      if (!response?.item || response?.item?.stage === PreviousYearStatus.CALCULATING) {
        return 500;
      }
      return false;
    },
    cacheTime: 0,
    enabled: isPreviousYears,
    refetchOnWindowFocus: false,
  });

export const useGetSatUserQuery = (): SatResponseType | undefined => {
  const data = useGetQuery<ResponseType<SatResponseType>>([QueryKeys.ANUAL_DECLARATION_QUIZ]);
  const info = data?.data;
  return info?.item;
};

interface UseLoaderProgressReturn {
  progress: number;
  timeRemaining: number;
}

const QUIZ_PROGRESS_KEY = 'quizProgress';
const QUIZ_TIME_REMAINING_KEY = 'quizElapsedTime';

const MIN_TIME_REMAINING = 0;
const MAX_PROGRESS = 99;
const MIN_PROGRESS = 0;
const MAX_TIME_REMAINING = 85;
const TIME_INCREMENT = 1000;
export const useLoaderProgress = (): UseLoaderProgressReturn => {
  const initialProgress = Number(StorageService.getItem(QUIZ_PROGRESS_KEY) || `${MIN_PROGRESS}`);
  const initialTimeRemaining = Math.min(
    Math.max(
      Number(StorageService.getItem(QUIZ_TIME_REMAINING_KEY) || `${MAX_TIME_REMAINING}`),
      MIN_TIME_REMAINING
    ),
    MAX_TIME_REMAINING
  );

  const [progress, setProgress] = useState(initialProgress);
  const [timeRemaining, setTimeRemaining] = useState(initialTimeRemaining);

  useEffect(() => {
    const progressIncrement = MAX_PROGRESS / MAX_TIME_REMAINING;

    const timeInterval = setInterval(() => {
      setTimeRemaining(prevTime => {
        const newTime = Math.max(prevTime - 1, MIN_TIME_REMAINING);
        if (newTime <= MIN_TIME_REMAINING) {
          clearInterval(timeInterval);
        }

        if (newTime >= MIN_TIME_REMAINING && newTime <= MAX_TIME_REMAINING) {
          StorageService.setItem(QUIZ_TIME_REMAINING_KEY, newTime.toString());
        }
        return newTime;
      });

      setProgress(() => {
        const calculatedProgress = (MAX_TIME_REMAINING - timeRemaining) * progressIncrement;
        const newProgress = Math.max(MIN_PROGRESS, Math.min(calculatedProgress, MAX_PROGRESS));
        const roundedProgress = Math.round(newProgress);

        if (roundedProgress >= MIN_PROGRESS && roundedProgress <= MAX_PROGRESS) {
          StorageService.setItem(QUIZ_PROGRESS_KEY, roundedProgress.toString());
        }
        return roundedProgress;
      });
    }, TIME_INCREMENT);

    const secondsInterval = setInterval(() => {
      if (timeRemaining <= MIN_TIME_REMAINING) {
        clearInterval(secondsInterval);
        return;
      }
      const currentMs = Number(StorageService.getItem(QUIZ_TIME_REMAINING_KEY) || '0');
      const newTime = Math.max(MIN_TIME_REMAINING, Math.min(currentMs + 1, MAX_TIME_REMAINING));

      if (newTime >= MIN_TIME_REMAINING && newTime <= MAX_TIME_REMAINING) {
        StorageService.setItem(QUIZ_TIME_REMAINING_KEY, newTime.toString());
      }
    }, TIME_INCREMENT);

    return () => {
      clearInterval(timeInterval);
      clearInterval(secondsInterval);
    };
  }, [timeRemaining]);

  return { progress, timeRemaining };
};
